import type {PublicationContext, Subscription} from 'centrifuge';
import {Centrifuge} from 'centrifuge';
import type {CollaborationChannelAccess, PosterCollaborationAccess, StreamTokenResponse} from '@PosterWhiteboard/collaboration/poster-presence.types';

const CONNECTION_TOKEN_ENDPOINT = 'stream/getStreamToken';
const COLLABORATION_ACCESS_ENDPOINT = 'stream/getPosterCollaborationAccess';
const WEBSOCKET_PATH = '/connection/websocket';

type ChannelName = 'edits' | 'presence';

/**
 * One tab's realtime connection for one poster: the server's admission (channel names and
 * subscription tokens) and the Centrifuge client both the presence and the live-edits sides
 * subscribe through. Admission is re-fetched whenever a token expires.
 */
export class PosterCollaborationConnection {
  #centrifuge: Centrifuge | null = null;
  #access: PosterCollaborationAccess | null = null;

  constructor(private readonly posterHashId: string) {}

  /** Fetches admission and connects. Throws when the server refuses, in which case nothing is connected. */
  async open(): Promise<PosterCollaborationAccess> {
    this.#access = await this.#fetchAccess();
    const centrifuge = new Centrifuge(`${window.PMW.streamClientUrl.replace(/^http/, 'ws')}${WEBSOCKET_PATH}`, {getToken: fetchConnectionToken});
    centrifuge.connect();
    this.#centrifuge = centrifuge;
    return this.#access;
  }

  subscribe(channel: ChannelName, onPublication: (context: PublicationContext) => void): Subscription {
    const centrifuge = this.#centrifuge;
    const access = this.#access;
    if (centrifuge === null || access === null) {
      throw new Error('PosterCollaborationConnection: subscribe before open');
    }
    const subscription = centrifuge.newSubscription(this.#channelAccess(access, channel).channel, {
      token: this.#channelAccess(access, channel).token,
      getToken: async (): Promise<string> => this.#channelAccess(await this.#refreshAccess(), channel).token,
    });
    subscription.on('publication', onPublication);
    subscription.subscribe();
    return subscription;
  }

  /** Unsubscribes one channel; the connection stays open for the other side. */
  unsubscribe(subscription: Subscription): void {
    subscription.unsubscribe();
    this.#centrifuge?.removeSubscription(subscription);
  }

  close(): void {
    this.#centrifuge?.disconnect();
    this.#centrifuge = null;
    this.#access = null;
  }

  async #refreshAccess(): Promise<PosterCollaborationAccess> {
    this.#access = await this.#fetchAccess();
    return this.#access;
  }

  async #fetchAccess(): Promise<PosterCollaborationAccess> {
    return (await window.PMW.readLocal(COLLABORATION_ACCESS_ENDPOINT, {hashedId: this.posterHashId})) as PosterCollaborationAccess;
  }

  #channelAccess(access: PosterCollaborationAccess, channel: ChannelName): CollaborationChannelAccess {
    return channel === 'edits' ? access.edits : access.presence;
  }
}

const fetchConnectionToken = async (): Promise<string> => {
  const response = (await window.PMW.readLocal(CONNECTION_TOKEN_ENDPOINT)) as StreamTokenResponse;
  return response.token;
};
